/**
 * Editing a goods draft — #90.
 *
 * A sibling of `/decas/$decaId` rather than a branch of the create route, and mounted on the
 * **write** surface: editing is a write, and the router already draws that line structurally
 * instead of leaving it to a conditional inside a read screen. The URL also settles the
 * document's family without a discriminator — this is a goods route the way
 * `/hojas-de-ruta/$sheetId/editar` is a route-sheet one — so nothing has to ask an id what it is.
 *
 * The screen itself is only a loader: it reads the draft with its `ETag` and hands both to
 * `GoodsCreateForm`, which is the same form that created it. The `ETag` matters because every
 * write against an existing document requires `If-Match`, and `GET /decas/{id}` is the only
 * place one is handed out.
 */

import { Navigate, useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { useAuth } from '../app/auth.tsx'
import { useDeca } from '../app/queries.ts'
import { isGenerated } from '../deca/estado.ts'
import { abilitiesFor } from '../deca/roles.ts'
import { AppShell } from '../shell/AppShell.tsx'
import { Card, ErrorState, LoadingRows, NoAccessState } from '../ui/primitives.tsx'
import { GoodsCreateForm } from './GoodsCreateForm.tsx'

export function DecaEditScreen() {
  const { t } = useTranslation()
  const { decaId } = useParams({ strict: false }) as { decaId: string }
  const { principal } = useAuth()
  const abilities = abilitiesFor(principal)

  const query = useDeca(decaId)
  const deca = query.data?.data
  const etag = query.data?.etag ?? undefined

  if (!abilities.writeDecas) {
    return (
      <AppShell title={t('detail.draftTitle')}>
        <Card>
          <NoAccessState />
        </Card>
      </AppShell>
    )
  }

  if (query.isLoading) {
    return (
      <AppShell title={t('detail.draftTitle')}>
        <Card>
          <LoadingRows rows={6} columns={3} />
        </Card>
      </AppShell>
    )
  }

  if (query.error || !deca) {
    return (
      <AppShell title={t('detail.draftTitle')}>
        <Card>
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        </Card>
      </AppShell>
    )
  }

  // "Only while `BORRADOR`". A generated DeCA is never edited in place: it changes through
  // *Modificar* (BOE Fifth.1) or *Regenerar como documento nuevo* (Fifth.2), and Seventh voids
  // any other alteration. `PATCH /decas/{decaId}` answers 409 for exactly this, but nobody
  // should have to submit a form to be told, and a URL typed, bookmarked or followed after the
  // document was generated must not open an editor over it. Sent to the document rather than
  // shown a refusal: there is nothing to edit here, and everything it is legitimate to do to a
  // generated document is on that screen. `replace`, so Back does not bounce into the editor.
  if (isGenerated(deca)) {
    return <Navigate to="/decas/$decaId" params={{ decaId }} replace />
  }

  return (
    <AppShell title={t('detail.draftTitle')}>
      <GoodsCreateForm draft={deca} etag={etag} />
    </AppShell>
  )
}
